import type { Grant } from './access';

export interface PermissionDefinition {
  key: Exclude<keyof Grant, 'resource' | 'visibility'>;
  label: string;
  accessibleLabel: string;
  description: string;
  group: 'Gestion' | 'Validation' | 'Suppression';
}
const create: PermissionDefinition = {
  key: 'canCreate',
  label: 'Créer',
  accessibleLabel: 'Créer',
  description: 'Ajouter de nouvelles fiches dans ce module.',
  group: 'Gestion',
};
const update: PermissionDefinition = {
  key: 'canUpdate',
  label: 'Modifier',
  accessibleLabel: 'Modifier',
  description: 'Mettre à jour les données des fiches accessibles.',
  group: 'Gestion',
};
const remove: PermissionDefinition = {
  key: 'canDelete',
  label: 'Supprimer',
  accessibleLabel: 'Supprimer',
  description: 'Supprimer les fiches dans le périmètre autorisé.',
  group: 'Suppression',
};
const validate: PermissionDefinition = {
  key: 'canValidate',
  label: 'Valider',
  accessibleLabel: 'Valider',
  description: 'Valider ou refuser les demandes accessibles.',
  group: 'Validation',
};

// Only capabilities enforced by the current API are shown. New capabilities get
// a definition in their module and a matching API guard, never a new table column.
export const modulePermissions = (resource: string): PermissionDefinition[] => {
  switch (resource) {
    case 'task-delegation':
      return [
        {
          ...update,
          label: 'Déléguer des tâches',
          accessibleLabel: 'Déléguer des tâches',
          description: 'Attribuer une tâche à un collaborateur autorisé.',
        },
      ];
    case 'tasks':
      return [create, update, remove];
    case 'candidate-trash':
      return [
        {
          ...update,
          label: 'Restaurer les candidats',
          accessibleLabel: 'Restaurer les candidats',
          description: 'Rétablir les fiches supprimées dans le périmètre autorisé de la corbeille.',
        },
      ];
    case 'dashboard':
    case 'prospection':
      return [];
    case 'users':
      return [
        {
          ...create,
          label: 'Créer un utilisateur',
          description: 'Créer un compte interne à la plateforme.',
        },
        {
          ...update,
          label: 'Modifier le rôle d’un utilisateur',
          description: 'Changer son rôle, avec le droit de modification des permissions.',
        },
      ];
    case 'permissions':
      return [
        {
          ...create,
          label: 'Définir les droits d’un nouveau rôle',
          description: 'Configurer les permissions lors de la création d’un rôle.',
        },
        {
          ...update,
          label: 'Modifier et attribuer les droits',
          description:
            'Modifier les permissions d’un rôle ou l’attribuer à un collaborateur autorisé.',
        },
      ];
    case 'roles':
      return [
        {
          ...create,
          label: 'Créer un rôle',
          description:
            'Créer un rôle. Définir ses droits nécessite aussi la création de permissions.',
        },
        {
          ...update,
          label: 'Renommer un rôle',
          description: 'Modifier le nom et la description d’un rôle.',
        },
        {
          ...remove,
          label: 'Supprimer un rôle',
          description: 'Supprimer un rôle personnalisé qui n’est plus attribué.',
        },
      ];
    case 'teams':
      return [
        { ...create, label: 'Créer une équipe', description: 'Créer un groupe de collaborateurs.' },
        {
          ...update,
          label: 'Gérer les membres',
          description: 'Modifier l’équipe et les collaborateurs qui partagent son portefeuille.',
        },
        {
          ...remove,
          label: 'Supprimer une équipe',
          description: 'Retirer le groupe et le partage de données associé.',
        },
      ];
    case 'submitted-timesheets':
      return [
        {
          ...update,
          label: 'Modifier les CRA soumis',
          accessibleLabel: 'Modifier les CRA soumis',
          description:
            'Corriger les journées et heures supplémentaires en attente de validation. Nécessite aussi le droit Modifier du module CRA. Les CRA validés restent verrouillés.',
        },
      ];
    case 'timesheets':
      return [create, update, validate, remove];
    case 'adv':
      return [
        create,
        update,
        {
          ...validate,
          label: 'Valider et émettre',
          description: 'Valider ou émettre les documents accessibles.',
        },
        remove,
      ];
    default:
      return [create, update, remove];
  }
};
